import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

export interface DiscordServer {
  id: string;
  discord_guild_id: string;
  discord_guild_name: string;
  discord_guild_icon: string | null;
  vault_id: string;
  vaults?: any;
  metadata?: Record<string, any>;
}

export interface DiscordChannel {
  id: string;
  discord_channel_id: string;
  discord_channel_name: string;
  discord_guild_id: string;
  pydechat_channel_id: string | null;
}

export function useDiscordServers() {
  const [servers, setServers] = useState<DiscordServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchServers = async () => {
      try {
        setLoading(true);
        const session = await supabase.auth.getSession();
        const token = session?.data?.session?.access_token;

        if (!token) {
          setServers([]);
          setLoading(false);
          return;
        }

        const response = await fetch('https://psydechatthing.onrender.com/api/discord/servers', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          // Non-fatal: Discord servers just won't load
          setServers([]);
          setLoading(false);
          return;
        }

        const data = await response.json() as { servers?: DiscordServer[] };
        setServers(data.servers || []);
        setError(null);
      } catch (err) {
        // Non-fatal: Discord servers just won't load
        console.warn('Discord servers unavailable:', err);
        setServers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServers();
  }, []);

  return { servers, loading, error };
}

export function useDiscordChannels(guildId: string | null) {
  const [channels, setChannels] = useState<DiscordChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!guildId) {
      setChannels([]);
      return;
    }

    const fetchChannels = async () => {
      try {
        setLoading(true);
        const session = await supabase.auth.getSession();
        const token = session?.data?.session?.access_token;

        if (!token) {
          setChannels([]);
          setLoading(false);
          return;
        }

        const response = await fetch(`https://psydechatthing.onrender.com/api/discord/servers/${guildId}/channels`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          // Non-fatal: Discord channels just won't load
          setChannels([]);
          setLoading(false);
          return;
        }

        const data = await response.json() as { channels?: DiscordChannel[] };
        setChannels(data.channels || []);
        setError(null);
      } catch (err) {
        // Non-fatal: Discord channels just won't load
        console.warn('Discord channels unavailable:', err);
        setChannels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChannels();
  }, [guildId]);

  return { channels, loading, error };
}

export async function linkDiscordServer(
  discord_guild_id: string,
  discord_guild_name: string,
  discord_guild_icon: string | null,
  vault_id: string | null
) {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch('https://psydechatthing.onrender.com/api/discord/link-server', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      discord_guild_id,
      discord_guild_name,
      discord_guild_icon,
      ...(vault_id && { vault_id }),
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to link Discord server: ${response.statusText}`);
  }

  return await response.json();
}

export async function manageWhitelist(
  discord_guild_id: string,
  target_user_id: string,
  action: 'add' | 'remove'
) {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch('https://psydechatthing.onrender.com/api/discord/whitelist', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      discord_guild_id,
      target_user_id,
      action,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to manage whitelist: ${response.statusText}`);
  }

  return await response.json();
}
